/**
 * ReclassifyChip — small inline "Edit type" affordance on an Oracle Recent
 * Uploads row. Lets a compliance user correct the artifact type PSI inferred
 * for a document; the existing /api/overmind/intake/:id/reclassify endpoint
 * persists the correction AND feeds the POM learning engine, so future
 * documents that look similar get the corrected type.
 *
 * UX contract (matches Mandate's EditChip):
 *   - calm grey "Edit type" pill, no emoji, no gradients
 *   - click -> small white card with dropdown + optional reason
 *   - keyboard accessible (Tab, Enter, Esc)
 *   - on failure, dropdown stays open with inline error
 *
 * This component intentionally does NOT say "train the AI" / "teach PSI".
 * It reads as editing a field. PSI quietly learns underneath.
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function ReclassifyChip({ docId, currentType, options, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState(currentType || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!docId) return null;

  const cancel = () => {
    setEditing(false);
    setChosen(currentType || '');
    setReason('');
    setError(null);
  };

  const submit = async () => {
    setError(null);
    if (!chosen || chosen === currentType) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/overmind/intake/${docId}/reclassify`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artifact_type: chosen,
          reason: reason || undefined,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.message || 'Could not save. Please try again.');
      }
      setEditing(false);
      setBusy(false);
      if (typeof onSaved === 'function') onSaved(chosen);
    } catch (e) {
      setBusy(false);
      setError(e.message || 'Could not save. Please try again.');
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        onKeyDown={(e) => { if (e.key === 'Enter') setEditing(true); }}
        aria-label="Edit document type"
        title="Edit type — PSI will use this correction on future similar documents."
        style={{
          background: 'transparent',
          border: '1px solid #cbd5e1',
          borderRadius: 10,
          padding: '0 7px',
          marginLeft: 6,
          fontSize: 10,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#475569',
          cursor: 'pointer',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          height: 18,
          lineHeight: '16px',
          verticalAlign: 'middle',
          flexShrink: 0,
        }}
      >
        Edit type
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Correct document type"
      onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
      style={{
        display: 'block',
        marginTop: 8,
        padding: '8px 10px',
        background: '#ffffff',
        border: '1px solid #cbd5e1',
        minWidth: 260,
      }}
    >
      <div style={{ fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', marginBottom: 4, fontFamily: 'ui-monospace, monospace' }}>
        Correct document type
      </div>

      <select
        value={chosen}
        onChange={(e) => setChosen(e.target.value)}
        autoFocus
        style={{ width: '100%', padding: '4px 6px', fontSize: 13, border: '1px solid #cbd5e1', background: '#ffffff' }}
      >
        <option value="">— pick the correct type —</option>
        {(options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label || o.value}</option>
        ))}
      </select>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why? (optional — helps PSI remember the reason)"
        rows={2}
        maxLength={240}
        style={{ width: '100%', marginTop: 6, padding: '4px 6px', fontSize: 12, border: '1px solid #cbd5e1', background: '#ffffff', resize: 'vertical' }}
      />

      {error && (
        <div role="alert" style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '4px 10px', fontSize: 12, color: '#475569', cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !chosen}
          style={{ background: '#1e293b', color: '#ffffff', border: '1px solid #1e293b', padding: '4px 12px', fontSize: 12, cursor: busy ? 'wait' : 'pointer' }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
